/**
 * Juridictions (province + ville) par pays, pour les sélecteurs de localisation
 * du profil entreprise. RDC ('CD') par défaut ; d'autres pays peuvent être
 * ajoutés en étendant JURISDICTIONS.
 *
 * Ces valeurs alimentent `company.address.province` / `company.address.city`,
 * consommées par l'isolation juridictionnelle des administrateurs régionaux.
 */

export interface ProvinceOption {
  /** Valeur stockée (== label pour rester lisible côté données). */
  value: string;
  label: string;
  /** Villes principales de la province (chef-lieu en tête). */
  cities: string[];
}

export interface CountryJurisdiction {
  countryCode: string;
  countryName: string;
  provinces: ProvinceOption[];
}

/** Pays par défaut (RDC). */
export const DEFAULT_JURISDICTION_COUNTRY = 'CD';

/**
 * Les 26 provinces de la RDC avec leurs principales villes (chef-lieu en tête).
 */
const DRC_PROVINCES: ProvinceOption[] = [
  { value: 'Kinshasa', label: 'Kinshasa', cities: ['Kinshasa'] },
  { value: 'Kongo-Central', label: 'Kongo-Central', cities: ['Matadi', 'Boma', 'Muanda', 'Mbanza-Ngungu'] },
  { value: 'Kwango', label: 'Kwango', cities: ['Kenge', 'Kahemba', 'Kasongo-Lunda'] },
  { value: 'Kwilu', label: 'Kwilu', cities: ['Bandundu', 'Kikwit', 'Idiofa'] },
  { value: 'Mai-Ndombe', label: 'Mai-Ndombe', cities: ['Inongo', 'Nioki', 'Kutu'] },
  { value: 'Kasaï', label: 'Kasaï', cities: ['Tshikapa', 'Ilebo', 'Mweka'] },
  { value: 'Kasaï-Central', label: 'Kasaï-Central', cities: ['Kananga', 'Demba', 'Dibaya'] },
  { value: 'Kasaï-Oriental', label: 'Kasaï-Oriental', cities: ['Mbuji-Mayi', 'Mwene-Ditu', 'Tshilenge'] },
  { value: 'Lomami', label: 'Lomami', cities: ['Kabinda', 'Mwene-Ditu', 'Ngandajika'] },
  { value: 'Sankuru', label: 'Sankuru', cities: ['Lusambo', 'Lodja', 'Lomela'] },
  { value: 'Maniema', label: 'Maniema', cities: ['Kindu', 'Kasongo', 'Kalima'] },
  { value: 'Sud-Kivu', label: 'Sud-Kivu', cities: ['Bukavu', 'Uvira', 'Baraka', 'Kamituga'] },
  { value: 'Nord-Kivu', label: 'Nord-Kivu', cities: ['Goma', 'Butembo', 'Beni', 'Rutshuru'] },
  { value: 'Ituri', label: 'Ituri', cities: ['Bunia', 'Aru', 'Mahagi', 'Mambasa'] },
  { value: 'Haut-Uele', label: 'Haut-Uele', cities: ['Isiro', 'Watsa', 'Dungu'] },
  { value: 'Bas-Uele', label: 'Bas-Uele', cities: ['Buta', 'Aketi', 'Bondo'] },
  { value: 'Tshopo', label: 'Tshopo', cities: ['Kisangani', 'Yangambi', 'Ubundu'] },
  { value: 'Mongala', label: 'Mongala', cities: ['Lisala', 'Bumba', 'Bongandanga'] },
  { value: 'Nord-Ubangi', label: 'Nord-Ubangi', cities: ['Gbadolite', 'Businga', 'Mobayi-Mbongo'] },
  { value: 'Sud-Ubangi', label: 'Sud-Ubangi', cities: ['Gemena', 'Zongo', 'Libenge'] },
  { value: 'Équateur', label: 'Équateur', cities: ['Mbandaka', 'Bikoro', 'Ingende'] },
  { value: 'Tshuapa', label: 'Tshuapa', cities: ['Boende', 'Ikela', 'Monkoto'] },
  { value: 'Tanganyika', label: 'Tanganyika', cities: ['Kalemie', 'Kabalo', 'Moba'] },
  { value: 'Haut-Lomami', label: 'Haut-Lomami', cities: ['Kamina', 'Kaniama', 'Bukama'] },
  { value: 'Lualaba', label: 'Lualaba', cities: ['Kolwezi', 'Dilolo', 'Kapanga'] },
  { value: 'Haut-Katanga', label: 'Haut-Katanga', cities: ['Lubumbashi', 'Likasi', 'Kipushi', 'Kasumbalesa'] },
];

export const JURISDICTIONS: Record<string, CountryJurisdiction> = {
  CD: {
    countryCode: 'CD',
    countryName: 'République Démocratique du Congo',
    provinces: DRC_PROVINCES,
  },
};

/** Provinces d'un pays (RDC par défaut). Retourne [] si le pays n'est pas mappé. */
export function getProvinces(countryCode: string = DEFAULT_JURISDICTION_COUNTRY): ProvinceOption[] {
  return JURISDICTIONS[countryCode]?.provinces ?? [];
}

/** Villes d'une province (RDC par défaut). Retourne [] si introuvable. */
export function getCities(
  province: string,
  countryCode: string = DEFAULT_JURISDICTION_COUNTRY,
): string[] {
  const p = getProvinces(countryCode).find((x) => x.value === province);
  return p?.cities ?? [];
}
